import { useState, useEffect, useMemo } from 'react';
import {
  Select,
  Table,
  useFilters,
  useGetList,
  useQueryState,
  IconLoader,
  ButtonPseudo,
  ButtonSize,
  IconClose,
  ToggleInput,
  InputText,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetEmployeesApplicationSchemesQuery,
  useEditApplicationSchemeSettingsMutation,
} from '../store/services/application-shemes';
import {
  columns,
  columnsLabels,
  schema,
  filtersSchema,
} from './variables/list';
import { useLazyGetPSPDetailsQuery } from '../store/services/details';
import {
  ApplicatinSchemeType,
  ApplicationScheme,
  ApplicationSettingsEnum,
} from './types';
import { ApplicationSchemeSettingsKeys } from '../types';
import { setApplicationSchemasForSelect } from '../store/slices/application-shemes';

import cn from './styles.module.css';

let timeout: NodeJS.Timeout;

const formatApplicationSchemasList = (
  list: ApplicationScheme[],
): ApplicationScheme[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let applicationSchemeType = '';

    if (result.type === ApplicatinSchemeType.COMPANY_OFFICIAL) {
      applicationSchemeType = 'Company official';
    } else if (result.type === ApplicatinSchemeType.INDIVIDUAL) {
      applicationSchemeType = 'Individual';
    } else if (result.type === ApplicatinSchemeType.CORPORATE) {
      applicationSchemeType = 'Corporate';
    }

    const render: Record<string, React.JSX.Element> = {
      type: <div>{applicationSchemeType}</div>,
      active: <div>{result.active ? 'Active' : 'Inactive'}</div>,
      visible: <div>{result.visible ? 'Visible' : 'Not visible'}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const ApplicationsSchemes = () => {
  const [individualScheme, setIndividualScheme] = useState('');
  const [corporateScheme, setCorporateScheme] = useState('');
  const [cofScheme, setCOFScheme] = useState('');
  const [uboSettings, setUboSettings] = useState({
    scheme: '',
    percentage: '',
  });

  const dispatch = useAppDispatch();

  const [
    createCorporateOrganizationByCustomerAvailable,
    setCreateCorporateOrganizationByCustomerAvailable,
  ] = useState(false);
  const [
    createIndividualOrganizationByCustomerAvailable,
    setCreateIndividualOrganizationByCustomerAvailable,
  ] = useState(false);

  const [getPspDetails] = useLazyGetPSPDetailsQuery();

  const { filters, onFilterChange } = useQueryState(schema);

  const [getEmployeesApplicationSchemesList, { isLoading, isFetching }] =
    useLazyGetEmployeesApplicationSchemesQuery();
  const [edit, { isLoading: editIsLoading }] =
    useEditApplicationSchemeSettingsMutation();

  const [changeSetting] = useEditApplicationSchemeSettingsMutation();

  const { getListAction, onFilter, errorList } = useGetList<ApplicationScheme>({
    getList: getEmployeesApplicationSchemesList,
    onFilterChange,
    onSortChange: () => {},
    schema,
  });

  const {
    settings: {
      applicationSchemes: {
        applicationSchemas,
        applicationSchemasForSelect,
        errorsList,
      },
      details: { pspDetails },
    },
  } = useAppSelector((state) => state);

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatApplicationSchemasList(
      applicationSchemas as ApplicationScheme[],
    );

    return data;
  }, [applicationSchemas]);

  const onEditSettings = async (
    type: ApplicatinSchemeType,
    scheme?: string,
  ) => {
    if (type === ApplicatinSchemeType.INDIVIDUAL) {
      await edit({
        applicationSchemeIndividualId: scheme,
        key: ApplicationSchemeSettingsKeys.APPLICATION_SCHEME_INDIVIDUAL,
      });
    } else if (type === ApplicatinSchemeType.CORPORATE) {
      await edit({
        applicationSchemeCorporateId: scheme,
        key: ApplicationSchemeSettingsKeys.APPLICATION_SCHEME_CORPORATE,
      });
    } else if (type === ApplicatinSchemeType.COMPANY_OFFICIAL) {
      await edit({
        applicationSchemeCompanyOfficialId: scheme,
        key: ApplicationSchemeSettingsKeys.APPLICATION_SCHEME_COMPANY_OFFICIAL,
      });
    }
  };

  const onEditUBOSettings = async (
    type: 'uboScheme' | 'uboPercentage',
    scheme?: string,
  ) => {
    if (type === 'uboScheme') {
      await edit({
        applicationSchemeCompanyOfficialUboId: scheme || null,
        key: ApplicationSchemeSettingsKeys.APPLICATION_SCHEME_COMPANY_OFFICIAL_UBO,
      });
    } else if (type === 'uboPercentage') {
      await edit({
        minimumUboOwnershipPercentage: scheme ? Number(scheme) : null,
        key: ApplicationSchemeSettingsKeys.MINIMUM_UBO_OWNERSHIP_PERCENTAGE,
      });
    }
  };

  const onChangeApplicationCustomerSettings = async ({
    checked,
    type,
  }: {
    checked: boolean;
    type: string;
  }) => {
    if (type === 'createIndividualOrganizationByCustomerAvailable') {
      setCreateIndividualOrganizationByCustomerAvailable(checked);

      await changeSetting({
        createIndividualOrganizationByCustomerAvailable: checked,
        key: ApplicationSettingsEnum.CREATE_INDIVIDUAL_ORGANIZATION_BY_CUSTOMER_AVAILABLE,
      });
    } else if (type === 'createCorporateOrganizationByCustomerAvailable') {
      setCreateCorporateOrganizationByCustomerAvailable(checked);

      await changeSetting({
        createCorporateOrganizationByCustomerAvailable: checked,
        key: ApplicationSettingsEnum.CREATE_CORPORATE_ORGANIZATION_BY_CUSTOMER_AVAILABLE,
      });
    }
  };

  useEffect(() => {
    getListAction({
      filters,
    });
  }, [filters]);

  useEffect(() => {
    if (!pspDetails) {
      getPspDetails();
    }
  }, [pspDetails]);

  useEffect(() => {
    if (!applicationSchemasForSelect && applicationSchemas) {
      dispatch(setApplicationSchemasForSelect(applicationSchemas));
    }
  }, [applicationSchemasForSelect, applicationSchemas]);

  useEffect(() => {
    if (pspDetails) {
      const individualSchemeSetting = pspDetails?.find(
        (item) =>
          item.key ===
          ApplicationSchemeSettingsKeys.APPLICATION_SCHEME_INDIVIDUAL,
      );
      const corporatechemeSetting = pspDetails?.find(
        (item) =>
          item.key ===
          ApplicationSchemeSettingsKeys.APPLICATION_SCHEME_CORPORATE,
      );
      const cofSchemeSetting = pspDetails?.find(
        (item) =>
          item.key ===
          ApplicationSchemeSettingsKeys.APPLICATION_SCHEME_COMPANY_OFFICIAL,
      );
      const uboScheme = pspDetails?.find(
        (item) =>
          item.key ===
          ApplicationSchemeSettingsKeys.APPLICATION_SCHEME_COMPANY_OFFICIAL_UBO,
      );
      const uboPercentage = pspDetails?.find(
        (item) =>
          item.key ===
          ApplicationSchemeSettingsKeys.MINIMUM_UBO_OWNERSHIP_PERCENTAGE,
      );

      const createIndividualOrganizationByCustomerAvailableSetting =
        pspDetails.find(
          (item) =>
            item.key ===
            ApplicationSettingsEnum.CREATE_INDIVIDUAL_ORGANIZATION_BY_CUSTOMER_AVAILABLE,
        );
      const createCorporateOrganizationByCustomerAvailableSetting =
        pspDetails.find(
          (item) =>
            item.key ===
            ApplicationSettingsEnum.CREATE_CORPORATE_ORGANIZATION_BY_CUSTOMER_AVAILABLE,
        );

      if (individualSchemeSetting) {
        setIndividualScheme(individualSchemeSetting?.data?.value as string);
      }

      if (corporatechemeSetting) {
        setCorporateScheme(corporatechemeSetting?.data?.value as string);
      }

      if (cofSchemeSetting) {
        setCOFScheme(cofSchemeSetting?.data?.value as string);
      }

      if (uboScheme || uboPercentage) {
        setUboSettings({
          scheme: (uboScheme?.data?.value as string) || '',
          percentage:
            uboPercentage?.data?.value !== null &&
            uboPercentage?.data?.value !== undefined
              ? uboPercentage?.data?.value?.toString()
              : '',
        });
      }

      if (createIndividualOrganizationByCustomerAvailableSetting) {
        setCreateIndividualOrganizationByCustomerAvailable(
          createIndividualOrganizationByCustomerAvailableSetting?.data
            ?.value as boolean,
        );
      }

      if (createCorporateOrganizationByCustomerAvailableSetting) {
        setCreateCorporateOrganizationByCustomerAvailable(
          createCorporateOrganizationByCustomerAvailableSetting?.data
            ?.value as boolean,
        );
      }
    }
  }, [pspDetails]);

  useEffect(() => {
    let title = 'Settings: Applications';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.applicationSchemeLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <>
        <div className={cn.applicationsSchemesTitle}>Settings</div>
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div className={cn.formGroup}>
            <Select
              name="individualScheme"
              value={individualScheme}
              list={
                applicationSchemasForSelect
                  ?.filter(
                    (item) =>
                      item.type === ApplicatinSchemeType.INDIVIDUAL &&
                      item.active,
                  )
                  ?.map((item) => ({ name: item.name, value: item.id })) || []
              }
              onChange={(e) => {
                setIndividualScheme(e.target.value as string);
                onEditSettings(
                  ApplicatinSchemeType.INDIVIDUAL,
                  e.target.value as string,
                );
              }}
              selectWrapperClassName={cn.selectWrapper}
              disabled={editIsLoading}
              placeholder="Select default individual scheme"
              title="Default individual scheme"
              fullWidth
            />
            {individualScheme && (
              <div className={cn.removeValueWrapper}>
                <ButtonPseudo
                  size={ButtonSize.SMALL}
                  text="Remove"
                  icon={<IconClose />}
                  onClick={() => {
                    onEditSettings(ApplicatinSchemeType.INDIVIDUAL, undefined);
                    setIndividualScheme('');
                  }}
                />
              </div>
            )}
          </div>
          <div className={cn.formGroup}>
            <Select
              name="corporateScheme"
              value={corporateScheme}
              list={
                applicationSchemasForSelect
                  ?.filter(
                    (item) =>
                      item.type === ApplicatinSchemeType.CORPORATE &&
                      item.active,
                  )
                  ?.map((item) => ({ name: item.name, value: item.id })) || []
              }
              onChange={(e) => {
                setCorporateScheme(e.target.value as string);
                onEditSettings(
                  ApplicatinSchemeType.CORPORATE,
                  e.target.value as string,
                );
              }}
              disabled={editIsLoading}
              selectWrapperClassName={cn.selectWrapper}
              placeholder="Select default corporate scheme"
              title="Default corporate scheme"
              fullWidth
            />
            {corporateScheme && (
              <div className={cn.removeValueWrapper}>
                <ButtonPseudo
                  size={ButtonSize.SMALL}
                  text="Remove"
                  icon={<IconClose />}
                  onClick={() => {
                    onEditSettings(ApplicatinSchemeType.CORPORATE, undefined);
                    setCorporateScheme('');
                  }}
                />
              </div>
            )}
          </div>
          <div className={cn.formGroup}>
            <Select
              name="cofScheme"
              value={cofScheme}
              list={
                applicationSchemasForSelect
                  ?.filter(
                    (item) =>
                      item.type === ApplicatinSchemeType.COMPANY_OFFICIAL &&
                      item.active,
                  )
                  ?.map((item) => ({ name: item.name, value: item.id })) || []
              }
              onChange={(e) => {
                setCOFScheme(e.target.value as string);
                onEditSettings(
                  ApplicatinSchemeType.COMPANY_OFFICIAL,
                  e.target.value as string,
                );
              }}
              selectWrapperClassName={cn.selectWrapper}
              disabled={editIsLoading}
              placeholder="Select default cof scheme"
              title="Default COF scheme"
              fullWidth
            />
            {cofScheme && (
              <div className={cn.removeValueWrapper}>
                <ButtonPseudo
                  size={ButtonSize.SMALL}
                  text="Remove"
                  icon={<IconClose />}
                  onClick={() => {
                    onEditSettings(
                      ApplicatinSchemeType.COMPANY_OFFICIAL,
                      undefined,
                    );
                    setCOFScheme('');
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div
          className={cn.applicationBeneficialWrapper}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <div className={cn.applicationBeneficialTitle}>
            Ultimate Beneficial Owner (UBO)
          </div>
          <div className={cn.applicationBeneficialFormGroup}>
            <Select
              name="uboScheme"
              value={uboSettings.scheme}
              list={
                applicationSchemasForSelect
                  ?.filter(
                    (item) =>
                      item.type === ApplicatinSchemeType.COMPANY_OFFICIAL &&
                      item.active,
                  )
                  ?.map((item) => ({ name: item.name, value: item.id })) || []
              }
              onChange={(e) => {
                setUboSettings({
                  ...uboSettings,
                  scheme: e.target.value as string,
                });

                onEditUBOSettings('uboScheme', e.target.value as string);
              }}
              selectWrapperClassName={cn.selectWrapper}
              disabled={editIsLoading}
              placeholder="Select default COF UBO scheme"
              title="Default COF UBO scheme"
              withClearButton
              fullWidth
            />
          </div>
          <div className={cn.applicationBeneficialFormGroup}>
            <InputText
              name="uboPercentage"
              value={uboSettings?.percentage}
              title="Minimum UBO ownership percentage"
              placeholder="Enter percentage"
              onChange={(e) => {
                clearTimeout(timeout);

                let formattedValue;

                if (Number(e.target.value) < 0) {
                  formattedValue = '0';
                } else if (Number(e.target.value) > 100) {
                  formattedValue = '100';
                } else {
                  formattedValue = e.target.value;
                }

                setUboSettings({
                  ...uboSettings,
                  percentage: formattedValue,
                });

                timeout = setTimeout(() => {
                  onEditUBOSettings('uboPercentage', formattedValue);
                }, 500);
              }}
            />
            <div className={cn.applicationBeneficialPercentageHint}>
              From 0 to 100
            </div>
          </div>
        </div>
        <div className={cn.applicationsSchemesTitle}>
          Organization creation by customer
        </div>
        <div className={cn.formGroup}>
          <ToggleInput
            name="createIndividualOrganizationByCustomerAvailable"
            checked={createIndividualOrganizationByCustomerAvailable}
            title="Individual organizations"
            onChange={(e) =>
              onChangeApplicationCustomerSettings({
                checked: e.target.checked,
                type: 'createIndividualOrganizationByCustomerAvailable',
              })
            }
          />
        </div>
        <div className={cn.formGroup}>
          <ToggleInput
            name="createCorporateOrganizationByCustomerAvailable"
            checked={createCorporateOrganizationByCustomerAvailable}
            title="Corporate organizations"
            onChange={(e) =>
              onChangeApplicationCustomerSettings({
                checked: e.target.checked,
                type: 'createCorporateOrganizationByCustomerAvailable',
              })
            }
          />
        </div>
        <div className={cn.applicationSchemeListWrapper}>
          <div className={cn.applicationsSchemesTitle}>Application Schemes</div>
          <Table
            columns={columns}
            data={formattedData || []}
            onSort={() => {}}
            filters={filters}
            sort={{}}
            columnsLabels={columnsLabels}
            error={errorsList || errorList}
            tableFormClassName={cn.applicationTable}
            tableTopClassName={cn.beneficiaryTableTop}
            onFilterChange={(values) =>
              onFilter({
                ...values,
              })
            }
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isLoading || isFetching}
            clickable
          />
        </div>
      </>
    );
  }

  return content;
};

export default ApplicationsSchemes;
